import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import { formatPhoneNumber } from 'shared/utils';
import { useCallback } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { LinkType } from 'shared/types';
import { LinkTypes } from '../../constants';

type FormLinksBaseProps = {
  labelKey: string;
  linksField: 'links' | 'internalLinks';
  expandField?: 'expandLinks' | 'expandInternalLinks';
};

const typeOptions: { label: string; value: LinkType }[] = [
  { label: 'Phone', value: LinkTypes.phone },
  { label: 'Website', value: LinkTypes.website },
  { label: 'Email', value: LinkTypes.email },
];

export const FormLinksBase = (props: FormLinksBaseProps): JSX.Element => {
  const { labelKey, linksField, expandField } = props;
  const { t } = useTranslation();
  const { control, formState, getValues, setValue, watch, trigger } =
    useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: linksField,
  });

  const expandLinks = expandField ? watch(expandField, false) : true;

  const debouncedFieldValidation = useDebounceCallback(trigger, 350);

  const handleOnChangeValue = (value: string, index: number): void => {
    const linkType = getValues(`${linksField}.${index}.linkType`);

    if (linkType !== LinkTypes.phone) {
      setValue(`${linksField}.${index}.value`, value);
      debouncedFieldValidation(`${linksField}.${index}.value`);
      return;
    }

    const nextValue = formatPhoneNumber(value);
    setValue(`${linksField}.${index}.value`, nextValue);
    debouncedFieldValidation(`${linksField}.${index}.value`);
  };

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const links = Array.from(getValues(linksField));
      const [reorderedItem] = links.splice(result.source.index, 1);
      links.splice(result.destination.index, 0, reorderedItem);
      setValue(linksField, links);
    },
    [getValues, linksField, setValue],
  );

  return (
    <>
      <Grid size={12}>
        {expandField ? (
          <Button
            variant="text"
            color="inherit"
            startIcon={
              expandLinks ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />
            }
            onClick={() => setValue(expandField, !expandLinks)}
            disabled={formState.isSubmitting}
            aria-label={
              expandLinks
                ? t('createEditLocation.collapseLinks')
                : t('createEditLocation.expandLinks')
            }
          >
            <Typography variant="h3">{t(labelKey)}</Typography>
          </Button>
        ) : (
          <Typography variant="h3">{t(labelKey)}</Typography>
        )}
      </Grid>

      <Grid
        size={12}
        sx={{ display: expandLinks ? 'block' : 'none' }}
        data-testid="links-section"
      >
        <Stack spacing={2} useFlexGap>
          {fields.length > 0 && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="links">
                {(provided) => (
                  <Stack
                    spacing={2}
                    useFlexGap
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {fields.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(prov) => (
                          <Stack
                            spacing={2}
                            useFlexGap
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            ref={prov.innerRef}
                            sx={{
                              flexDirection: 'row',
                            }}
                          >
                            <MenuIcon
                              color="secondary"
                              sx={{ mt: { sm: 2 } }}
                            />

                            <Grid
                              container
                              spacing={2}
                              size="grow"
                              sx={{ width: '100%' }}
                            >
                              <Grid size={{ xs: 12, sm: 2 }}>
                                <Controller
                                  name={`${linksField}.${index}.linkType`}
                                  control={control}
                                  render={({ field, fieldState }) => {
                                    const {
                                      ref,
                                      value: val,
                                      ...muiFieldProps
                                    } = field;
                                    return (
                                      <TextField
                                        select
                                        id={`${linksField}.${index}.linkType`}
                                        label={t(
                                          'formLinks.inputs.linkType.label',
                                        )}
                                        fullWidth
                                        {...muiFieldProps}
                                        value={val || ''}
                                        inputRef={ref}
                                        error={Boolean(fieldState.error)}
                                        helperText={fieldState.error?.message}
                                        required
                                        disabled={formState.isSubmitting}
                                      >
                                        {typeOptions.map(({ label, value }) => {
                                          return (
                                            <MenuItem
                                              key={label}
                                              value={value}
                                              sx={{
                                                textTransform: 'capitalize',
                                              }}
                                            >
                                              {label}
                                            </MenuItem>
                                          );
                                        })}
                                      </TextField>
                                    );
                                  }}
                                />
                              </Grid>

                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Controller
                                  name={`${linksField}.${index}.label`}
                                  control={control}
                                  render={({ field, fieldState }) => {
                                    const { ref, ...muiFieldProps } = field;
                                    return (
                                      <TextField
                                        id={`${linksField}.${index}.label`}
                                        label={t(
                                          'formLinks.inputs.linkLabel.label',
                                        )}
                                        fullWidth
                                        {...muiFieldProps}
                                        inputRef={ref}
                                        error={Boolean(fieldState.error)}
                                        helperText={fieldState.error?.message}
                                        required
                                        disabled={formState.isSubmitting}
                                      />
                                    );
                                  }}
                                />
                              </Grid>

                              <Grid size={{ xs: 12, sm: 'grow' }}>
                                <Controller
                                  name={`${linksField}.${index}.value`}
                                  control={control}
                                  render={({ field, fieldState }) => {
                                    const { ref, onChange, ...muiFieldProps } =
                                      field;
                                    return (
                                      <TextField
                                        id={`${linksField}.${index}.value`}
                                        label={t(
                                          'formLinks.inputs.linkValue.label',
                                        )}
                                        fullWidth
                                        {...muiFieldProps}
                                        onChange={(e) =>
                                          handleOnChangeValue(
                                            e.target.value,
                                            index,
                                          )
                                        }
                                        inputRef={ref}
                                        error={Boolean(fieldState.error)}
                                        helperText={fieldState.error?.message}
                                        required
                                        disabled={formState.isSubmitting}
                                      />
                                    );
                                  }}
                                />
                              </Grid>

                              <Grid
                                size={{ xs: 12, sm: 'auto' }}
                                sx={{ mt: 1, textAlign: 'end' }}
                              >
                                <IconButton
                                  color="error"
                                  onClick={() => remove(index)}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Stack>
                        )}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          )}
          <Button
            size="large"
            onClick={() => append({ linkType: null, label: '', value: '' })}
            sx={{
              width: { xs: '100%', sm: 'fit-content' },
            }}
            color="neutral"
          >
            {t('formLinks.button')}
          </Button>
        </Stack>
      </Grid>
    </>
  );
};
